/**
 * ============================================================================
 * Calculator Demo Page
 * ============================================================================
 * 
 * Page de démonstration des nouveaux composants Calculator:
 * - CategoryCard (cartes enrichies avec benchmark)
 * - CategoryDetail (liste FE avec état complété)
 * - EmissionDrawer (formulaire saisie)
 * - LastSaveIndicator (indicateur de sauvegarde)
 * - Persistance localStorage
 * 
 * Cette page sera supprimée après intégration dans Calculator.tsx
 * 
 * ============================================================================
 */

import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/DashboardLayout';
import { CategoryCard } from '@/components/calculator/CategoryCard';
import { CategoryDetail, EmissionFactor } from '@/components/calculator/CategoryDetail';
import { EmissionDrawer } from '@/components/calculator/EmissionDrawer';
import { LastSaveIndicator } from '@/components/calculator/LastSaveIndicator';
import { useDemoEmissions } from '@/hooks/useDemoEmissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';
import { AlertCircle, ArrowLeft, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

type ViewMode = 'categories' | 'detail' | 'form';

export default function CalculatorDemo() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFactor, setSelectedFactor] = useState<EmissionFactor | null>(null);

  // Demo emissions management
  const {
    emissions,
    lastSaveTime,
    addEmission,
    clearAllEmissions,
    getEmissionsByCategory,
    getEmissionsByFactor,
    getCategoryMetrics,
    getTotalEmissions,
  } = useDemoEmissions();

  // Fetch emission factors
  const { data: factorsData, isLoading: loadingFactors, error } = trpc.calculator.getFactors.useQuery();
  const factors = factorsData || [];

  // Group factors by category
  const categorizedFactors = useMemo(() => {
    if (!factors.length) return {};
    
    return factors.reduce((acc, factor) => {
      const category = factor.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(factor as EmissionFactor);
      return acc;
    }, {} as Record<string, EmissionFactor[]>);
  }, [factors]);

  // Get completed factors for selected category
  const completedFactorsMap = useMemo(() => {
    if (!selectedCategory) return new Map();
    
    const categoryEmissions = getEmissionsByCategory(selectedCategory);
    const map = new Map<string, { activity_value: number; tco2e: number }>();
    
    categoryEmissions.forEach(emission => {
      // Keep only the latest emission for each FE
      const existing = map.get(emission.fe_key);
      if (!existing || new Date(emission.created_at) > new Date(existing.activity_value.toString())) {
        map.set(emission.fe_key, {
          activity_value: emission.activity_value,
          tco2e: emission.tco2e,
        });
      }
    });
    
    return map;
  }, [selectedCategory, emissions, getEmissionsByCategory]);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setViewMode('detail');
  };

  const handleFactorSelect = (factor: EmissionFactor) => {
    setSelectedFactor(factor);
    setViewMode('form');
  };

  const handleBack = () => {
    if (viewMode === 'form') {
      setViewMode('detail');
      setSelectedFactor(null);
    } else if (viewMode === 'detail') {
      setViewMode('categories');
      setSelectedCategory(null);
    }
  };

  const handleSubmit = (data: { value: number; description?: string }) => {
    if (!selectedFactor) return;
    
    addEmission({
      fe_key: selectedFactor.fe_key,
      category: selectedFactor.category,
      subcategory: selectedFactor.subcategory,
      process_name: selectedFactor.process_name,
      unit: selectedFactor.unit,
      factor_value: selectedFactor.factor_value,
      activity_value: data.value,
      description: data.description,
    });
    
    toast.success(`Émission ajoutée : ${data.value} ${selectedFactor.unit || ''}`);
    setViewMode('detail');
    setSelectedFactor(null);
  };

  const handleClearAll = () => {
    if (confirm('Voulez-vous vraiment supprimer toutes les émissions de la démo ?')) {
      clearAllEmissions();
      toast.success('Toutes les émissions ont été supprimées');
    }
  };

  const totalEmissions = getTotalEmissions();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <CardTitle>Calculator Demo 🎨</CardTitle>
                  <LastSaveIndicator lastSaveTime={lastSaveTime} />
                </div>
                <CardDescription>
                  Démonstration des nouveaux composants Calculator avec persistance localStorage
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {viewMode !== 'categories' && (
                  <Button variant="outline" onClick={handleBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Retour
                  </Button>
                )}
                {emissions.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleClearAll}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Effacer tout
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Total Emissions Summary */}
        {totalEmissions > 0 && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total des émissions</p>
                  <p className="text-3xl font-bold mt-1">{totalEmissions.toFixed(2)} kg CO₂e</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">{emissions.length} émissions enregistrées</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Banner */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Cette page de démo utilise localStorage pour la persistance. Les données sont sauvegardées automatiquement dans votre navigateur.
          </AlertDescription>
        </Alert>

        {/* Content */}
        <Card>
          <CardContent className="p-6">
            {loadingFactors ? (
              <div className="text-center py-12 text-muted-foreground">
                Chargement des facteurs d'émission...
              </div>
            ) : error ? (
              <div className="text-center py-12 text-destructive">
                Erreur: {error.message}
              </div>
            ) : viewMode === 'categories' ? (
              /* Vue Catégories */
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Catégories d'émissions</h3>
                  <p className="text-sm text-muted-foreground">
                    Cliquez sur une catégorie pour voir les facteurs disponibles
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.entries(categorizedFactors).map(([category, categoryFactors]) => {
                    const metrics = getCategoryMetrics(category, categoryFactors.length);
                    return (
                      <CategoryCard
                        key={category}
                        category={category}
                        factorCount={metrics.factorCount}
                        completedCount={metrics.completedCount}
                        totalCO2={metrics.totalCO2}
                        benchmark={metrics.benchmark}
                        onClick={() => handleCategoryClick(category)}
                      />
                    );
                  })}
                </div>
              </div>
            ) : viewMode === 'detail' && selectedCategory ? (
              /* Vue Détail Catégorie */
              <CategoryDetail
                category={selectedCategory}
                factors={categorizedFactors[selectedCategory] || []}
                completedFactors={completedFactorsMap}
                onBack={handleBack}
                onSelectFactor={handleFactorSelect}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Emission Drawer */}
      <EmissionDrawer
        open={viewMode === 'form'}
        factor={selectedFactor}
        onClose={() => {
          setViewMode('detail');
          setSelectedFactor(null);
        }}
        onSubmit={handleSubmit}
      />
    </DashboardLayout>
  );
}

